import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Truck } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useFetch } from "../lib/api";
import { money, imgUrl } from "../lib/shop";
import { useQuote, Totals, DiscountBox } from "../components/site/Quote";
import { Seo } from "../components/site/Seo";

function CartLine({ item, available }) {
  const { updateQty, removeItem } = useStore();
  const id = `${item.slug}-${item.colour ? item.colour.replace(/\s+/g, "") + "-" : ""}${item.size.replace(/\s+/g, "")}`.toLowerCase();
  const c = item.colour || "";
  return (
    <div className="flex gap-4 py-5 border-b border-white/10" data-testid={`cart-item-${id}`}>
      <Link to={`/products/${item.slug}`} className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden bg-[#18181c] shrink-0">
        <img src={imgUrl(item.image)} alt={item.name} className="w-full h-full object-cover" />
      </Link>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500">{item.brand}</p>
        <Link to={`/products/${item.slug}`} className="font-bold leading-snug block truncate">{item.name}</Link>
        <p className="text-sm text-zinc-400 mt-1">{c && <>Colour: <span className="text-white font-semibold" data-testid={`cart-colour-${id}`}>{c}</span> · </>}Size: <span className="text-white font-semibold">{item.size}</span> · {money(item.price)}</p>
        {item.sku && <p className="text-[11px] text-zinc-500" data-testid={`cart-sku-${id}`}>SKU {item.sku}</p>}
        {available !== undefined && available < item.qty && <p className="text-xs text-red-400 mt-1">{available ? `Only ${available} left` : "SOLD OUT — please remove"}</p>}
        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center border border-white/15 rounded-full">
            <button className="w-10 h-10 grid place-items-center" onClick={() => updateQty(item.product_id, item.size, item.qty - 1, c)} data-testid={`cart-qty-decrease-${id}`} aria-label="Decrease"><Minus size={14} /></button>
            <span className="w-6 text-center text-sm font-bold" data-testid={`cart-qty-${id}`}>{item.qty}</span>
            <button className="w-10 h-10 grid place-items-center" onClick={() => updateQty(item.product_id, item.size, item.qty + 1, c)} data-testid={`cart-qty-increase-${id}`} aria-label="Increase"><Plus size={14} /></button>
          </div>
          <span className="font-extrabold">{money(item.price * item.qty)}</span>
          <button onClick={() => removeItem(item.product_id, item.size, c)} className="p-2 text-zinc-500 hover:text-red-400" data-testid={`cart-remove-${id}`} aria-label="Remove"><Trash2 size={18} /></button>
        </div>
      </div>
    </div>
  );
}

export default function Cart() {
  const { cart, zoneId, setZoneId } = useStore();
  const zones = useFetch("/delivery-zones");
  const quote = useQuote();
  const nav = useNavigate();
  if (!cart.length) {
    return (
      <div className="container-x py-24 text-center" data-testid="cart-empty">
        <Seo title="Your Cart" />
        <h1 className="font-display text-6xl">Your cart is empty</h1>
        <p className="text-zinc-400 mt-3">Find your next pair and step different.</p>
        <Link to="/shop" className="btn-gold mt-8" data-testid="cart-shop-now">Shop Now</Link>
      </div>
    );
  }
  const avail = (it) => quote?.lines.find((l) => l.product_id === it.product_id && l.size === it.size && (l.colour || "") === (it.colour || ""))?.available;
  return (
    <div className="container-x py-10 sm:py-16">
      <Seo title="Your Cart" />
      <h1 className="font-display text-6xl sm:text-8xl leading-none">Your Cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-10 mt-8">
        <div>{cart.map((it) => <CartLine key={it.product_id + (it.colour || "") + it.size} item={it} available={avail(it)} />)}</div>
        <aside className="card-dark p-6 h-fit lg:sticky lg:top-28 space-y-5">
          <div>
            <p className="label flex items-center gap-2"><Truck size={14} /> Delivery estimate</p>
            <select className="field" value={zoneId} onChange={(e) => setZoneId(e.target.value)} data-testid="cart-zone-select">
              <option value="">Choose delivery area</option>
              {(zones.data || []).map((z) => <option key={z.id} value={z.id}>{z.name} — {z.fee ? money(z.fee) : "FREE"}</option>)}
            </select>
          </div>
          <DiscountBox />
          {quote?.discount_error && <p className="text-xs text-red-400" data-testid="discount-error">{quote.discount_error}</p>}
          <Totals quote={quote} />
          {quote?.errors?.length > 0 && <p className="text-xs text-red-400" data-testid="cart-errors">{quote.errors.join(" · ")}</p>}
          <button className="btn-gold w-full" disabled={quote?.errors?.length > 0} onClick={() => nav("/checkout")} data-testid="cart-checkout-button">Checkout</button>
          <p className="text-xs text-zinc-500 text-center">Online store · FREE Uber delivery (Johannesburg) or PAXI collection at PEP. Prices in South African Rand (ZAR).</p>
        </aside>
      </div>
    </div>
  );
}
